// src/pages/CreateCharacterPage/CreateCharacterPage.tsx
import React, { useEffect, useState } from "react";
import charactersStore from "stores/charactersStore";
import userStore from "stores/userStore";
// import authStore from 'stores/authStore';
import { ICharacterDto } from "shared/interfaces";
import CharactersApiService from "services/api/CharactersApiService";

interface CreateCharacterPageProps {
  onBack: () => void;
  onCreated?: (updatedChars: ICharacterDto[]) => void;
}

const races = ["Антропоморф", "Мехатроник"];
const genders = ["Мужской", "Женский"];
const aspects = ["Сила", "Ловкость"];

const imageMap: Record<string, string> = {
  Антропоморф: "antr",
  Мехатроник: "mech",
  Мужской: "male",
  Женский: "female",
  Сила: "force",
  Ловкость: "agility",
};

const portraitMap: Record<string, Record<string, Record<string, string[]>>> = {
  Антропоморф: {
    Сила: {
      Мужской: ["1am", "2am", "3am", "4am"],
      Женский: ["1af", "2af", "3af", "4af"],
    },
    Ловкость: {
      Мужской: ["5am", "6am", "7am", "8am"],
      Женский: ["5af", "6af", "7af", "8af"],
    },
  },
  Мехатроник: {
    Сила: {
      Мужской: ["1mm", "2mm", "3mm", "4mm"],
      Женский: ["1mf", "2mf", "3mf", "4mf"],
    },
    Ловкость: {
      Мужской: ["5mm", "6mm", "7mm", "8mm"],
      Женский: ["5mf", "6mf", "7mf", "8mf"],
    },
  },
};

export default function CreateCharacterPage({
  onBack,
  onCreated,
}: CreateCharacterPageProps) {
  const [characterName, setCharacterName] = useState("");
  const [race, setRace] = useState<string | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [aspect, setAspect] = useState<string | null>(null);
  const [selectedPortrait, setSelectedPortrait] = useState<string | null>(null);
  const [portraitOptions, setPortraitOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Подгружаем профиль пользователя сразу
  useEffect(() => {
    if (!userStore.user) {
      userStore.getProfile();
    }
  }, []);

  useEffect(() => {
    if (race && gender && aspect) {
      const options = portraitMap[race]?.[aspect]?.[gender] || [];
      setPortraitOptions(options);
      setSelectedPortrait(options[0] || null);
    } else {
      setPortraitOptions([]);
      setSelectedPortrait(null);
    }
  }, [race, gender, aspect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const accountName = userStore.user?.username;
    if (!accountName) {
      setError("Пользователь не авторизован. Пожалуйста, войдите в систему.");
      return;
    }

    if (!race || !gender || !aspect || !selectedPortrait) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log("Создание персонажа:", {
        accountName,
        characterName,
        race,
        aspect,
        gender,
        "custom:portrait": selectedPortrait,
      });

      await CharactersApiService.createCharacter({
        accountName,
        characterName,
        race,
        aspect,
        gender,
        "custom:portrait": selectedPortrait,
      });

      await charactersStore.getCharacters();

      setSuccess("Персонаж успешно создан!");
      if (onCreated) onCreated(charactersStore.characters);

      // сброс формы
      setCharacterName("");
      setRace(null);
      setGender(null);
      setAspect(null);
      setSelectedPortrait(null);
    } catch (err: any) {
      console.error("Ошибка при создании персонажа:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Ошибка создания персонажа"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFlipCardSelection = (
    title: string,
    options: string[],
    selected: string | null,
    onSelect: (value: string) => void
  ) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <p>{title}:</p>
      <div style={{ display: "flex", gap: 20, justifyContent: "center" }}>
        {options.map((opt) => (
          <div
            key={opt}
            onClick={() => onSelect(opt)}
            style={{ perspective: 1000, cursor: "pointer" }}
          >
            <div
              style={{
                width: 160,
                height: 180,
                borderRadius: 12,
                transformStyle: "preserve-3d",
                transition: "transform 0.6s",
                transform:
                  selected === opt ? "rotateY(0deg)" : "rotateY(180deg)",
                position: "relative",
                boxShadow:
                  selected === opt ? "0 0 15px #00ff99" : "0 0 5px #00aaff",
                transformOrigin: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  backfaceVisibility: "hidden",
                  borderRadius: 12,
                  overflow: "hidden",
                  transform: "rotateY(0deg)",
                }}
              >
                <img
                  src={`/images/${imageMap[opt]}.png`}
                  alt={opt}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  backgroundColor: "#111122",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#00aaff",
                  fontWeight: "bold",
                  borderRadius: 12,
                }}
              >
                <img
                  src="/images/back.png"
                  alt="back"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0a0a1a",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "VT323, monospace",
        color: "#00aaff",
        padding: 10,
        boxSizing: "border-box",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: 750,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          backgroundColor: "#111122",
          padding: 20,
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <h2 style={{ textAlign: "center" }}>Создание нового персонажа</h2>

        <input
          value={characterName}
          onChange={(e) => setCharacterName(e.target.value)}
          placeholder="Имя персонажа"
          required
          style={{
            padding: 8,
            borderRadius: 8,
            border: "1px solid #00aaff",
            backgroundColor: "#0a0a1a",
            color: "#00aaff",
            fontSize: 16,
          }}
        />

        {renderFlipCardSelection("Раса", races, race, setRace)}
        {renderFlipCardSelection("Класс", aspects, aspect, setAspect)}
        {renderFlipCardSelection("Пол", genders, gender, setGender)}

        {race && gender && aspect && (
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              marginTop: 10,
            }}
          >
            {portraitOptions.map((p) => (
              <div
                key={p}
                onClick={() => setSelectedPortrait(p)}
                style={{
                  border:
                    selectedPortrait === p
                      ? "3px solid #00ff99"
                      : "2px solid #00aaff",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  transform:
                    selectedPortrait === p ? "scale(1.05)" : "scale(1)",
                  boxShadow:
                    selectedPortrait === p ? "0 0 10px #00ff99" : "none",
                }}
              >
                <img
                  src={`/images/${p}.png`}
                  alt={p}
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 10,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "2px solid #00aaff",
              backgroundColor: "transparent",
              color: "#00aaff",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Назад
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "#00aaff",
              color: "#111122",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {loading ? "Создание..." : "Создать"}
          </button>
        </div>

        {error && (
          <p style={{ color: "#ff0040", textAlign: "center", marginTop: 6 }}>
            {error}
          </p>
        )}
        {success && (
          <p style={{ color: "#00ff40", textAlign: "center", marginTop: 6 }}>
            {success}
          </p>
        )}
      </form>
    </div>
  );
}
